import React, { CSSProperties, FC, useState } from 'react';
import { Flex, Spinner, Text } from '@chakra-ui/react';

/**
 * The full-page loader, and it carries its own styles deliberately.
 *
 * `index.tsx` renders this as the Suspense fallback while the App chunk loads, and `styles.css` is
 * imported *inside* that chunk — so anything this component takes from the stylesheet is not there
 * yet when it first paints. Relying on it meant the raw 278px logo appeared unstyled in the corner
 * for as long as the chunk took, then snapped into place.
 *
 * So the geometry is inline, which applies with the element itself, and the keyframes ride along in
 * a `<style>` tag in the same chunk. Nothing here reaches for a class defined elsewhere.
 *
 * The mark is the Oryton "O" drawn as two arcs rather than the PNG, so the logo is its own spinner:
 * the navy C stays put and the red piece travels once round the ring, then rests at home for a beat
 * (Hai, 2026-10-28, option B of the preloader mockup). Arcs are measured in degrees via
 * `pathLength={360}`; a circle's path starts at three o'clock and runs clockwise.
 *
 * Dark mode is read from `data-theme`, which ColorModeScript sets on <html> before anything paints;
 * the stored navy sinks into the dark page, so it lifts there. The red keeps its colour.
 */
const SWEEP_MS = 1800;
const NAVY = '#34495E';
const NAVY_ON_DARK = '#8399AF';
const RED = '#E74B3C';

const PRELOADER_CSS = `
@keyframes ory-preloader-sweep { 0% { transform: rotate(0deg); } 72%, 100% { transform: rotate(360deg); } }
@keyframes ory-preloader-dim { 0%, 72%, 100% { opacity: 1; } 36% { opacity: .72; } }
:root[data-theme="dark"] .ory-preloader__navy circle { stroke: ${NAVY_ON_DARK}; }
@media (prefers-reduced-motion: reduce) {
    .ory-preloader__navy, .ory-preloader__red { animation: none !important; }
}
`;

const containerStyle: CSSProperties = {
    position: 'fixed',
    inset: 0,
    display: 'grid',
    placeItems: 'center'
};

const markStyle: CSSProperties = {
    width: 56,
    height: 56,
    overflow: 'visible'
};

const arcGroupStyle: CSSProperties = {
    transformBox: 'view-box',
    transformOrigin: '50% 50%'
};

/** Shared by both arcs: one ring, 38 units thick, on the 280-unit box the PNG was drawn on. */
const arcProps = { cx: 140, cy: 140, r: 119, fill: 'none', strokeWidth: 38, pathLength: 360 };

export const LoadingCenter = React.memo(() => {
    // The loader is unmounted and mounted again as the app moves from chunk loading to login to the
    // layout. Starting each one at the page clock's phase keeps the red piece where it was, instead
    // of jumping back home at every hand-over.
    const [phase] = useState(() => `-${Math.round(performance.now() % SWEEP_MS)}ms`);
    const timing = `${SWEEP_MS}ms ${phase} infinite`;

    return (
        <div style={containerStyle} role="status" aria-live="polite">
            <style>{PRELOADER_CSS}</style>
            <svg className="ory-preloader__mark" style={markStyle} viewBox="0 0 280 280" aria-hidden="true">
                <g
                    className="ory-preloader__navy"
                    style={{ ...arcGroupStyle, animation: `ory-preloader-dim ${timing} ease-in-out` }}
                >
                    {/* 54° to 306°: the C, open on the right */}
                    <circle {...arcProps} stroke={NAVY} strokeDasharray="252 108" strokeDashoffset={-54} />
                </g>
                <g
                    className="ory-preloader__red"
                    style={{ ...arcGroupStyle, animation: `ory-preloader-sweep ${timing} cubic-bezier(.7,0,.3,1)` }}
                >
                    {/* -35° to 35°: the piece in the opening */}
                    <circle {...arcProps} stroke={RED} strokeDasharray="70 290" strokeDashoffset={35} />
                </g>
            </svg>
        </div>
    );
});
LoadingCenter.displayName = 'LoadingCenter';

export const LoadingCentric: FC<{ msg: string }> = React.memo(({ msg = 'Loading...' }) => {
    return (
        <Flex minH="240px" align="center" justify="center" direction="column" gap={3}>
            <Spinner size="lg" />
            <Text color="gray.500">{msg}</Text>
        </Flex>
    );
});
LoadingCentric.displayName = 'LoadingCentric';
